import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import { V1Service } from '@zelos/codegen/apis';
import { getFormattedDate } from '@zelos/shared/lib/date-util';
import { useCommQueryData } from '@zelos/shared/lib/use-comm-query-data';
import Image from 'next/image';
import Link from 'next/link';
import { memo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BannerBoardLogo } from '../banner-board-components';
import Button from '@/components/comm/Button';
import DdayChip from '@/components/comm/DdayChip';
import Divider from '@/components/comm/Divider';
import EditorViewer from '@/components/comm/EditorViewer';
import MobileFloating from '@/components/comm/MobileFloating';
import MobileNav from '@/components/layout/gnb/MobileNav';
import { breakpointMedium, breakpointSmall } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import { testIds } from '@/constants/test-ids';
import { useBreakpoint } from '@/lib/breakpoint.service';
import { useImmediateHackleTrack } from '@/lib/hackle.service';
import icComputer from '@/public/images/ic-computer.svg';
import icProfile from '@/public/images/ic-profile.svg';
import icSubject from '@/public/images/ic-subject.svg';
import icTime from '@/public/images/ic-time.svg';

type JobDetailProps = {
  id: number,
  page?: number | null,
};

const JobTopDiv = styled.div`
display: flex;
align-items: center;
justify-content: space-between;
margin-top: 25px;
@media (max-width: ${breakpointMedium}) {
  display: grid;
  margin-bottom: 40px;
  row-gap: 16px;
}
`;

const JobTitleWrapDiv = styled.div`
display: flex;
align-items: center;
gap: 20px;
@media (max-width: ${breakpointSmall}) {
  align-items: start;
}
`;

const JobTitleDiv = styled.div`
display: grid;
grid-auto-rows: min-content;
row-gap: 8px;
@media (max-width: ${breakpointSmall}) {
  row-gap: 5px;
}
`;

const JobTitleSpan = styled.span`
color: ${colors.black};
font-size: 18px;
font-weight: 700;
line-height: 26px;
`;

const JobSubTitleSpan = styled.span`
color: ${colors.shadowBlue};
font-size: 16px;
font-weight: 500;
line-height: 23px;
`;

const LandingA = styled.a`
width: 262px;
height: 46px;
@media (max-width: ${breakpointMedium}) {
  width: auto;
  grid-column: 1 / 3;
}
& > button {
  width: 100%;
  height: 100%;
}
`;

const JobDivTitleDiv = styled.div`
display: flex;
height: 62px;
align-items: center;
color: ${colors.black};
font-size: 22px;
font-weight: 700;
line-height: 24px;
@media (max-width: ${breakpointSmall}) {
  font-size: 18px;
}
`;

const JobContentsWrapDiv = styled.div`
display: grid;
align-items: center;
padding: 20px 0;
grid-template-columns: 30px 140px auto;
row-gap: 24px;
@media (max-width: ${breakpointSmall}) {
  grid-template-columns: 30px 75px auto;
  row-gap: 15px;
}
`;

const JobContentIconDiv = styled.div`
display: flex;
height: fit-content;
grid-column: 1;
`;

const JobContentTitleSpan = styled.span`
color: ${colors.dark};
font-size: 15px;
font-weight: 700;
grid-column: 2;
line-height: 22px;
@media (max-width: ${breakpointSmall}) {
  font-size: 14px;
  line-height: 20px;
}
`;

const JobContentSpan = styled.span`
color: ${colors.black};
font-size: 15px;
font-weight: 400;
grid-column: 3;
line-height: 22px;

& > :not(:last-of-type) {
  margin-right: .25em;
}
@media (max-width: ${breakpointSmall}) {
  font-size: 14px;
  line-height: 21px;
}
`;

const JobContentChipWrapper = styled.div`
display: flex;
align-items: center;
gap: 8px;
`;

const JobMainContentWrapDiv = styled.div`
padding: 20px 0;
`;

const JobBottomDiv = styled.div`
display: flex;
justify-content: center;
margin-top: 120px;
`;

const JobDetail = ({ id, page }: JobDetailProps) => {
  useImmediateHackleTrack({
    event: {
      key: 'certi_job_viewed',
    },
  });

  const { data: jobData } = useQuery(
    ['v1JobsRetrieve', id],
    () => V1Service.v1JobsRetrieve({ id }),
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
    },
  );

  const breakpoint = useBreakpoint();

  const {
    findClassification,
    findLanguageNames,
  } = useCommQueryData();

  if (!jobData) return null;

  const {
    logo,
    position,
    companyName,
    landingUrl,
    career,
    careerMin,
    careerMax,
    demandEssentialSkill,
    essentialSkillClassification,
    essentialSkillLanguages,
    essentialSkillLevel,
    demandOptionalSkill,
    optionalSkillClassification,
    optionalSkillLanguages,
    optionalSkillLevel,
    demandOtherSkill,
    otherSkill,
    registrationEndDatetime,
    content,
  } = jobData;

  const requiredClassification = findClassification(essentialSkillClassification);
  const requiredLanguageNames = findLanguageNames(essentialSkillLanguages);

  const optionalClassification = findClassification(optionalSkillClassification);
  const optionalLanguageNames = findLanguageNames(optionalSkillLanguages);

  return (
    <div>
      <MobileNav icon="back" />
      <JobDivTitleDiv data-testid={testIds.jobs.JobDetail.Title}>
        <span>채용 정보</span>
      </JobDivTitleDiv>
      <Divider
        color={colors.black}
        thick={2}
      />
      <JobTopDiv>
        <JobTitleWrapDiv>
          <BannerBoardLogo
            src={logo.url}
            alt="logo"
          />
          <JobTitleDiv>
            <JobTitleSpan>{position}</JobTitleSpan>
            <JobSubTitleSpan>{companyName}</JobSubTitleSpan>
          </JobTitleDiv>
        </JobTitleWrapDiv>
        <LandingA
          href={landingUrl}
          target="_blank"
          rel="noreferrer"
        >
          <Button>
            기업채용정보 바로가기
          </Button>
        </LandingA>
      </JobTopDiv>
      <JobDivTitleDiv>
        <span>기본정보</span>
      </JobDivTitleDiv>
      <Divider
        color={colors.black}
        thick={2}
      />
      <JobContentsWrapDiv>
        <JobContentIconDiv>
          <Image src={icProfile} alt="profile" />
        </JobContentIconDiv>
        <JobContentTitleSpan>직무</JobContentTitleSpan>
        <JobContentSpan>{position}</JobContentSpan>
        <JobContentIconDiv>
          <Image src={icComputer} alt="comuter" />
        </JobContentIconDiv>
        <JobContentTitleSpan>모집구분</JobContentTitleSpan>
        {career === 'JR' && <JobContentSpan>신입</JobContentSpan>}
        {career === 'SR' && <JobContentSpan>{careerMin}년 ~ {careerMax}년</JobContentSpan>}
        <JobContentIconDiv>
          <Image src={icSubject} alt="subject" />
        </JobContentIconDiv>
        {demandEssentialSkill && (
          <>
            <JobContentTitleSpan>필수</JobContentTitleSpan>
            <JobContentSpan>
              {requiredClassification && <span>{requiredClassification.code}</span>}
              {requiredLanguageNames?.map((languageName) => <span key={uuidv4()}>{languageName}</span>)}
              {(requiredLanguageNames?.length || 0) > 1 && <span>중 택 1</span>}
              {essentialSkillLevel && <span>Lv{essentialSkillLevel} 이상</span>}
            </JobContentSpan>
          </>
        )}
        {demandOptionalSkill && (
          <>
            <JobContentTitleSpan>우대</JobContentTitleSpan>
            <JobContentSpan>
              {optionalClassification && <span>{optionalClassification.code}</span>}
              {optionalLanguageNames?.map((languageName) => <span key={uuidv4()}>{languageName}</span>)}
              {(optionalLanguageNames?.length || 0) > 1 && <span>중 택 1</span>}
              {optionalSkillLevel && <span>Lv{optionalSkillLevel} 이상</span>}
            </JobContentSpan>
          </>
        )}
        {demandOtherSkill && (
          <>
            <JobContentTitleSpan>기타</JobContentTitleSpan>
            <JobContentSpan>{otherSkill}</JobContentSpan>
          </>
        )}
        {registrationEndDatetime && (
          <>
            <JobContentIconDiv>
              <Image src={icTime} alt="time" />
            </JobContentIconDiv>
            <JobContentTitleSpan>지원 마감</JobContentTitleSpan>
            <JobContentChipWrapper>
              <JobContentSpan>
                {getFormattedDate(registrationEndDatetime, 'YYYY-MM-DD(dd) HH:mm')}
              </JobContentSpan>
              <DdayChip date={registrationEndDatetime} />
            </JobContentChipWrapper>
          </>
        )}
      </JobContentsWrapDiv>
      <JobDivTitleDiv>
        <span>공고 상세 내용</span>
      </JobDivTitleDiv>
      <Divider
        color={colors.black}
        thick={2}
      />
      <JobMainContentWrapDiv>
        <EditorViewer initialValue={content} />
      </JobMainContentWrapDiv>
      <Divider color={colors.lightGrey2} />
      {breakpoint === 'lg' && (
        <JobBottomDiv>
          <Link href={{ pathname: '/jobs', query: { page: page ?? 1 } }}>
            <Button width="260px" height="46px">
              목록으로
            </Button>
          </Link>
        </JobBottomDiv>
      )}
      {breakpoint !== 'lg' && (
        <MobileFloating>
          <Link href={{ pathname: '/jobs', query: { page: page ?? 1 } }}>
            <Button width="244px" height="43px">
              목록으로
            </Button>
          </Link>
        </MobileFloating>
      )}
    </div>
  );
};

export default memo(JobDetail);
